import { alsTag } from "./kalender";

/* ══════════════════════════════════════════════════════════════════
   Was ins Zeitraumfeld getippt und im Kalender geklickt wird — ohne
   Browser prüfbar (`bausteine/Zeitraum.tsx`).

   Prüfrunde Runde 11, „Neu gesehen": Getippt nahm das Feld nichts an, und
   im Kalender setzte der erste Klick das Ende statt des Anfangs; in der
   Form „Dauer" ließ sich der Anfang nicht nach vorn legen. Beides lag an
   zwei Stellen, die hier jetzt stehen:

   1. **Getipptes, das sich nicht lesen ließ, sprang still zurück.** Wer
      ins Feld klickte und tippte, schrieb hinter den vorbelegten Tag. Jetzt
      ist der alte Wert beim Hineinklicken markiert (`Kalenderfeld`), und
      was sich nicht lesen lässt, heißt `falsch` — das Feld wird rot.
   2. **Der zweite Klick wurde am Wert abgelesen** („Anfang ohne Ende →
      jetzt das Ende"). Die Vorbelegung der Vermietfläche ist genau so ein
      Wert. Jetzt merkt sich das Feld, ob der Anfang eben geklickt wurde.
   ══════════════════════════════════════════════════════════════════ */

/**
 * `12.10.` · `12.10.26` · `12.10.2026` — was am Telefon getippt wird.
 *
 * Auch mit Komma: Der Ziffernblock am Telefon (`inputMode="decimal"`) trägt
 * in deutscher Einstellung ein Komma und keinen Punkt, und „12,10,2026" ist
 * dann kein Tippfehler, sondern das, was die Tastatur hergibt.
 */
export function tagAusText(text: string, bezugsjahr: number): string | null {
  const m = /^(\d{1,2})\s*[.,\-/]\s*(\d{1,2})\s*[.,\-/]?\s*(\d{2,4})?$/.exec(text.trim());
  if (!m) return null;
  const [, tt, mm, jj] = m;
  const tag = Number(tt);
  const monat = Number(mm);
  if (tag < 1 || tag > 31 || monat < 1 || monat > 12) return null;
  let jahr = bezugsjahr;
  if (jj) jahr = jj.length === 2 ? 2000 + Number(jj) : Number(jj);
  const d = new Date(jahr, monat - 1, tag);
  // Der 31.02. wird von Date still zum 03.03. — das ist kein Datum,
  // das jemand gemeint hat.
  if (d.getMonth() !== monat - 1 || d.getDate() !== tag) return null;
  return alsTag(d);
}

/** Was aus dem getippten Text wird. */
export type Gelesen =
  | { art: "leer" }
  | { art: "gleich" }
  | { art: "falsch" }
  | { art: "spanne"; von: string; bis: string };

/**
 * Den getippten Text lesen. `anzeige` ist, was das Feld vorher zeigte —
 * unverändert heißt: nichts zu tun. Das ist mehr als eine Abkürzung: Bei
 * einer Vorbelegung ohne eigenes Ende zeigte das Feld „06.10.2026", und
 * jedes Verlassen machte daraus einen Zeitraum von einem Tag.
 */
export function spanneAusText(text: string, anzeige: string, jahr: number): Gelesen {
  const roh = text.trim();
  if (!roh) return { art: "leer" };
  if (roh === anzeige.trim()) return { art: "gleich" };
  const teile = roh.split(/\s*(?:–|-|bis)\s*/i).filter(Boolean);
  if (teile.length > 2) return { art: "falsch" };
  /* **Das Ende zuerst, weil es das Jahr trägt.** „05.05. – 07.05.2031" heißt
     Mai 2031 und nicht Mai dieses Jahrs: Wer nur hinten ein Jahr schreibt,
     meint es für beide (17.09.2026). */
  const bisRoh = teile[1] ? tagAusText(teile[1], jahr) : null;
  if (teile[1] && !bisRoh) return { art: "falsch" };
  const jahrVorn = bisRoh ? Number(bisRoh.slice(0, 4)) : jahr;
  let von = tagAusText(teile[0] ?? "", jahrVorn);
  if (!von) return { art: "falsch" };
  /* **Über den Jahreswechsel eins zurück.** „28.12. – 03.01.2027" meint den
     28. Dezember 2026. */
  if (bisRoh && von > bisRoh) von = tagAusText(teile[0] ?? "", jahrVorn - 1) ?? von;
  const bis = bisRoh ?? von;
  return { art: "spanne", von, bis: bis >= von ? bis : von };
}

/**
 * **Ein Klick in den Kalender.** Der erste setzt den Anfang, der zweite das
 * Ende; wer vor den Anfang klickt, meint einen neuen Anfang. Welcher Klick
 * der zweite ist, sagt `endeFolgt` — gemerkt vom Feld, nicht am Wert
 * abgelesen. Nur ein Wert **ganz ohne Ende** (ein Feld, das noch auf sein
 * Ende wartet) nimmt den ersten Klick als Ende, wie bisher.
 */
export function kalenderKlick(
  wert: { von: string | null; bis: string | null },
  iso: string,
  endeFolgt: boolean,
): { neu: { von: string | null; bis: string | null }; endeFolgt: boolean; fertig: boolean } {
  const endeKlick = Boolean(wert.von) && iso >= (wert.von ?? "") && (endeFolgt || !wert.bis);
  if (!endeKlick) return { neu: { von: iso, bis: null }, endeFolgt: true, fertig: false };
  return { neu: { von: wert.von, bis: iso }, endeFolgt: false, fertig: true };
}
